import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';

import * as DatasourceActions from './datasource.actions';
import { DatasourceClientService } from '@mode/shared/data-access-webapp';
import { AccountFacade } from '@mode/accounts/contract';

@Injectable()
export class DatasourceEffects {
  loadDatasources$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DatasourceActions.loadDatasources),
      withLatestFrom(this.accountFacade.currentOrg$),
      concatMap(([action, org]) => {
        if (org.user) {
          return this.datasourceClient.getPublicDatasources();
        } else {
          return combineLatest([
            this.datasourceClient.getPublicDatasources(),
            this.datasourceClient.getDatasources(org.username),
          ]).pipe(
            map(([publicDS, orgDS]) => {
              return [...orgDS, ...publicDS];
            })
          );
        }
      }),
      map((datasources) => {
        return datasources.map((datasource) => {
          datasource.displayName = datasource.displayName.replace(/<[^>]*>/g, '');
          datasource.name = datasource.name.replace(/<[^>]*>/g, '');
          return datasource;
        });
      }),
      map((datasources) => DatasourceActions.loadDatasourcesSuccess({ datasources })),
      catchError((error) => of(DatasourceActions.loadDatasourcesFailure({ error })))
    );
  });

  loadReportDatasources$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DatasourceActions.loadReportDatasources),
      withLatestFrom(this.accountFacade.reportOwner$),
      concatMap(([action, org]) => {
        if (org.user) {
          return this.datasourceClient.getPublicDatasources();
        } else {
          return combineLatest([
            this.datasourceClient.getPublicDatasources(),
            this.datasourceClient.getDatasources(org.username),
          ]).pipe(
            map(([publicDS, orgDS]) => {
              return [...orgDS, ...publicDS];
            })
          );
        }
      }),
      map((datasources) => {
        return datasources.map((datasource) => {
          datasource.displayName = datasource.displayName.replace(/<[^>]*>/g, '');
          datasource.name = datasource.name.replace(/<[^>]*>/g, '');
          return datasource;
        });
      }),
      map((datasources) => DatasourceActions.loadReportDatasourcesSuccess({ datasources })),
      catchError((error) => of(DatasourceActions.loadReportDatasourcesFailure({ error })))
    );
  });

  refreshDatasource$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DatasourceActions.refreshDatasource),
      concatMap((action) => {
        return this.datasourceClient.refreshDatasource(action.token).pipe(
          map(() => DatasourceActions.refreshDatasourceSuccess({ token: action.token })),
          catchError((error) => of(DatasourceActions.refreshDatasourceFailure({ error, token: action.token })))
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    public accountFacade: AccountFacade,
    private datasourceClient: DatasourceClientService
  ) {}
}
